import clsx from "clsx"
import { useEffect, useState } from "react"

import EmptyRow from "@/ui/components/EmptyRow"
import Skeleton from "@/ui/components/Skeleton"
import Button from "@/ui/components/Button"
import * as TrinityAPI from "@/requests/TrinityAPI"
import { ModalPopUp } from "@/components/common/element/ModalPopUp"

import { BankTableItem } from "./Item"
import { BanksForm } from "./Form"

import style from "./style.module.scss"

import type { FC } from "react"
import type { ICompanyBank } from "@/types/company/IFullCompanyDto"
import type { IErrorModel } from "@/types/error/IErrorModel"
import type { IModalMessage } from "@/types/error/IModalMessage"

type Props = {
  companyId: string
  banks?: Array<ICompanyBank>
  isLoading: boolean
  refetch: () => void
}

export const BanksTable: FC<Props> = ({ banks, companyId, isLoading, refetch }) => {
  const [isCreateOpen, setCreateOpen] = useState(false)
  const [bankData, setBankData] = useState<ICompanyBank | null>()
  const [errors, setErrors] = useState<IErrorModel>()
  const [isSaving, setSaving] = useState(false)
  const [modalMessage, setModalMessage] = useState<IModalMessage | null>(null)

  const toggleCreateForm = () => {
    setCreateOpen(!isCreateOpen)
  }

  const handleCreateBank = () => {
    if (isSaving || !bankData) return

    setSaving(true)

    const payload = {
      ...bankData,
      id: null,
      companyId: Number(companyId)
    }

    TrinityAPI.createCompanyBank(
      payload,
      response => {
        if (response.status === 200) {
          setCreateOpen(false)
          refetch()
        } else {
          setErrors(response.data)
        }
      },
      () => setSaving(false)
    )
  }

  const handleRemoveBank = (bankId: number) => {
    TrinityAPI.removeCompanyBank(companyId, String(bankId), response => {
      if (response.status === 200) {
        refetch()
      } else {
        setModalMessage({ title: "Ошибка!", text: "Ошибка удаления компании: " + response.data.message })
      }
    })
  }

  useEffect(() => {
    setBankData(null)
  }, [isCreateOpen])

  return (
    <>
      <ModalPopUp message={modalMessage} close={() => setModalMessage(null)} />

      <div className="main-content--wrapper">
        <section className="main-content">
          <div className={style.actionButton}>
            {isCreateOpen ? (
              <>
                <Button onClick={handleCreateBank} loading={isSaving}>
                  Сохранить
                </Button>
                <Button variant="black" onClick={toggleCreateForm}>
                  Отменить
                </Button>
              </>
            ) : (
              <Button onClick={toggleCreateForm}>Добавить новый банк</Button>
            )}
          </div>
          {isCreateOpen && <BanksForm bank={bankData} setBank={setBankData} errors={errors} />}
          <ul className="header-global-list">
            <li className="header-global-item cols-4">Название</li>
            <li className="header-global-item cols-4">SWIFT</li>
            <li className="header-global-item cols-4">Номер счёта</li>
            <li className="header-global-item cols-4">Валюта счёта</li>
            <li className={clsx("header-global-item cols-4 text-end", style.itemButtons)}>Действия</li>
          </ul>
          <ul className="global-list">
            {isLoading && (
              <div className="skeleton-wrapper">
                <Skeleton rows={4} gutterTop />
              </div>
            )}
            {!isLoading &&
              ((banks?.length || 0) > 0 ? (
                banks?.map(bank => (
                  <BankTableItem onRemove={handleRemoveBank} bank={bank} key={bank.id} refetch={refetch} />
                ))
              ) : (
                <EmptyRow text="Банков не найдено" />
              ))}
          </ul>
        </section>
      </div>
    </>
  )
}
